import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Stack, Switch, Tooltip } from 'src/core/ui';
import { useAvailableAutopricingModes } from 'src/hooks/useAvailableAutopricingModes';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { RestrictSelectionType } from 'src/modals/ListingDetails/components/PricingSection/AutoPricingSection.types';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  AutoPricingCompListingMode,
  AutoPricingInputs,
  AutoPricingOutlierMode,
  AutoPricingUndercutMode,
  Feature,
  Listing,
  SectionInfo,
  UiCurrency,
} from 'src/WebApiController';

import { AutoPricingCompFloorAndCeilingInput } from '../AutoPricingCompFloorAndCeilingInput/AutoPricingCompFloorAndCeilingInput';
import { AutoPricingCompListingModeSelector } from '../AutoPricingCompListingModeSelector/AutoPricingCompListingModeSelector';
import { AutoPricingCompListingRowOffset } from '../AutoPricingCompListingRowOffset/AutoPricingCompListingRowOffset';
import { AutoPricingCompQuantitiesSelectorV2 } from '../AutoPricingCompQuantitiesSelector/AutoPricingCompQuantitiesSelectorV2';
import { AutoPricingOutlierDetectionModeSelector } from '../AutoPricingOutlierDetectionModeSelector/AutoPricingOutlierDetectionModeSelector';
import { AutoPricingOutlierDetectionSettingsInput } from '../AutoPricingOutlierDetectionSettingsInput/AutoPricingOutlierDetectionSettingsInput';
import { AutoPricingSelectedSectionSettingsInput } from '../AutoPricingSelectedSectionSettingsInput/AutoPricingSelectedSectionSettingsInput';
import { useAutoPricingSettingsSelectedSections } from '../AutoPricingSelectedSectionSettingsInput/AutoPricingSelectedSectionSettingsInput.hooks';
import { AutoPricingUnderCutModeSelector } from '../AutoPricingUnderCutModeSelector/AutoPricingUnderCutModeSelector';
import { AutoPricingUnderCutReferenceListingPositionInput } from '../AutoPricingUnderCutReferenceListingPositionInput/AutoPricingUnderCutReferenceListingPositionInput';
import { AutoPricingUnderCutRelativeAndAbsoluteAmountsInput } from '../AutoPricingUnderCutRelativeAndAbsoluteAmountsInput/AutoPricingUnderCutRelativeAndAbsoluteAmountsInput';
import * as styles from './AutoPricingSettingsFormContent.css';

export type AutoPricingSettingsFormContentCoreProps = {
  venueMapSections: SectionInfo[] | null | undefined;
  hasSeatScores: boolean;
  uiCurrency: UiCurrency;
  allowSeatScoreCompListingMode: boolean;
  disabled: boolean;
};

type AutoPricingSettingsFormContentCoreAndOptionalProps =
  AutoPricingSettingsFormContentCoreProps & {
    isGlobalEdit?: boolean;
    listing?: Listing;
    hideCompListingQuantityFilters?: boolean;
    hideUnderCutAmountInput?: boolean;
    hideBaseAdvancedSettingElements?: boolean;
    hideUnmappedInventoryToggle?: boolean;
    autoPricingSectionChildren?: React.ReactNode;
    priceAgainstSectionChildren?: React.ReactNode;
    pricingProtectionSectionChildren?: React.ReactNode;
    undercuttingSectionChildren?: React.ReactNode;
    advancedSettingsSectionChildren?: React.ReactNode;
    pricerTeamSectionChildren?: React.ReactNode;
  };

export const AutoPricingSettingsFormContent = ({
  listing,
  venueMapSections,
  hasSeatScores,
  isGlobalEdit,
  uiCurrency,
  allowSeatScoreCompListingMode,
  hideCompListingQuantityFilters,
  hideUnderCutAmountInput,
  hideBaseAdvancedSettingElements,
  hideUnmappedInventoryToggle,
  autoPricingSectionChildren,
  priceAgainstSectionChildren,
  pricingProtectionSectionChildren,
  undercuttingSectionChildren,
  advancedSettingsSectionChildren,
  pricerTeamSectionChildren,
  disabled,
}: AutoPricingSettingsFormContentCoreAndOptionalProps) => {
  const { watch } = useFormContext<AutoPricingInputs>();

  const { undercutMode, compListingMode } = watch();

  const { outlierModes, outlierModesDisabled, restrictSelectionTypes } =
    useAvailableAutopricingModes(
      true,
      undercutMode,
      compListingMode,
      hasSeatScores,
      false,
      isGlobalEdit,
      true
    );

  return (
    <div>
      {pricerTeamSectionChildren && (
        <AutoPricingSettingsSection title={ContentId.Pricers}>
          {pricerTeamSectionChildren}
        </AutoPricingSettingsSection>
      )}

      {autoPricingSectionChildren && (
        <AutoPricingSettingsSection title={ContentId.Autopricing}>
          {autoPricingSectionChildren}
        </AutoPricingSettingsSection>
      )}

      <PriceAgainstSection
        listing={listing}
        venueMapSections={venueMapSections}
        allowSeatScoreCompListingMode={allowSeatScoreCompListingMode}
        restrictSelectionTypes={restrictSelectionTypes}
        hideCompListingQuantityFilters={hideCompListingQuantityFilters}
        hideUnderCutAmountInput
        uiCurrency={uiCurrency}
        disabled={disabled}
      >
        {priceAgainstSectionChildren}
      </PriceAgainstSection>

      {pricingProtectionSectionChildren && (
        <AutoPricingSettingsSection title={ContentId.PricingProtection}>
          {pricingProtectionSectionChildren}
        </AutoPricingSettingsSection>
      )}

      <UndercuttingSection
        uiCurrency={uiCurrency}
        hideUnderCutAmountInput={hideUnderCutAmountInput}
        disabled={disabled}
      >
        {undercuttingSectionChildren}
      </UndercuttingSection>

      <AdvancedSettingsSection
        outlierModes={outlierModes}
        outlierModesDisabled={outlierModesDisabled}
        disabled={disabled}
        hideBaseAdvancedSettingElements={hideBaseAdvancedSettingElements}
        hideUnmappedInventoryToggle={hideUnmappedInventoryToggle}
      >
        {advancedSettingsSectionChildren}
      </AdvancedSettingsSection>
    </div>
  );
};

const PriceAgainstSection = ({
  listing,
  venueMapSections,
  allowSeatScoreCompListingMode,
  restrictSelectionTypes,
  uiCurrency,
  hideCompListingQuantityFilters,
  hideUnderCutAmountInput,
  disabled,
  children,
}: React.PropsWithChildren & {
  listing?: Listing;
  venueMapSections: SectionInfo[] | null | undefined;
  allowSeatScoreCompListingMode: boolean;
  restrictSelectionTypes: Record<string, ContentId>;
  uiCurrency: UiCurrency;
  hideCompListingQuantityFilters: boolean | undefined;
  hideUnderCutAmountInput: boolean | undefined;
  disabled: boolean;
}) => {
  const { watch, setValue } = useFormContext<AutoPricingInputs>();

  const { onRowOffsetChange } =
    useAutoPricingSettingsSelectedSections(venueMapSections);

  const {
    compListingMode,
    compListingOnlyForSameZoneEnabled,
    compListingSelectedSectionSettings,
    compListingQuantityFilters,
    compListingQuantityScoreAdjustmentOverrideJson,
  } = watch();

  const showCompListingSettingsDetails =
    compListingMode === AutoPricingCompListingMode.QualityScore;

  const showCompListingRestictSelectionDetails =
    compListingMode != null &&
    (compListingMode === AutoPricingCompListingMode.QualityScore ||
      compListingMode === AutoPricingCompListingMode.SameZone);

  const [
    isOnlyComparableZonesModeAvailable,
    setIsOnlyComparableZonesModeAvailable,
  ] = useState(false);

  const [selectedSectionsEnabled, setSelectedSectionsEnabled] =
    useState<boolean>(false);

  const compListingOnlyForSelectedSectionsEnabled = useMemo(() => {
    return (
      selectedSectionsEnabled ||
      Boolean(compListingSelectedSectionSettings?.sectionIdFilter?.length) ||
      isOnlyComparableZonesModeAvailable
    );
  }, [
    compListingSelectedSectionSettings?.sectionIdFilter?.length,
    isOnlyComparableZonesModeAvailable,
    selectedSectionsEnabled,
  ]);

  const setSelectedSectionsEnabledAndSectionSettings = useCallback(
    (compListingOnlyForSelectedSectionsEnabledNew: boolean | null) => {
      setSelectedSectionsEnabled(
        Boolean(compListingOnlyForSelectedSectionsEnabledNew)
      );

      if (!compListingOnlyForSelectedSectionsEnabledNew) {
        setValue('compListingSelectedSectionSettings', null);
      }
    },
    [setValue]
  );

  const restrictSelection = useMemo(
    () =>
      Boolean(
        compListingOnlyForSameZoneEnabled ||
        compListingOnlyForSelectedSectionsEnabled
      ),
    [
      compListingOnlyForSameZoneEnabled,
      compListingOnlyForSelectedSectionsEnabled,
    ]
  );

  delete restrictSelectionTypes[RestrictSelectionType.SameSection];

  return (
    <AutoPricingSettingsSection title={ContentId.PriceAgainst}>
      {!isOnlyComparableZonesModeAvailable && (
        <>
          <PricingSetting
            header={<Content id={ContentId.AllListings} />}
            detail={
              <AutoPricingCompListingModeSelector
                allowSeatScoreCompListingMode={allowSeatScoreCompListingMode}
                disabled={disabled}
                hideWhenOnlyComparableZonesModeIsAvailable={true}
                setIsOnlyComparableZonesModeAvailable={
                  setIsOnlyComparableZonesModeAvailable
                }
              />
            }
          />

          {showCompListingSettingsDetails && (
            <PricingSetting
              header={null}
              detail={
                <AutoPricingCompFloorAndCeilingInput disabled={disabled} />
              }
            />
          )}

          {showCompListingRestictSelectionDetails && (
            <>
              <PricingSetting
                header={<Content id={ContentId.RestrictSelection} />}
                detail={
                  <Switch
                    disabled={disabled}
                    checked={restrictSelection}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={(isChecked) => {
                      let compListingOnlyForSameZoneEnabledNew: boolean | null =
                        null;
                      let compListingOnlyForSelectedSectionsEnabledNew:
                        | boolean
                        | null = null;
                      if (isChecked) {
                        if (
                          !compListingOnlyForSameZoneEnabled &&
                          !compListingOnlyForSelectedSectionsEnabled
                        ) {
                          // set zone by default
                          compListingOnlyForSameZoneEnabledNew = true;
                          compListingOnlyForSelectedSectionsEnabledNew = false;
                        }
                      } else {
                        compListingOnlyForSameZoneEnabledNew = false;
                        compListingOnlyForSelectedSectionsEnabledNew = false;
                      }

                      if (
                        compListingOnlyForSameZoneEnabledNew != null ||
                        compListingOnlyForSelectedSectionsEnabledNew != null
                      ) {
                        setValue(
                          'compListingOnlyForSameZoneEnabled',
                          compListingOnlyForSameZoneEnabledNew
                        );
                        setSelectedSectionsEnabledAndSectionSettings(
                          compListingOnlyForSelectedSectionsEnabledNew
                        );
                      }
                    }}
                  />
                }
              />
            </>
          )}
          {restrictSelection && (
            <PricingSetting
              header={<Content id={ContentId.RestrictTo} />}
              detail={
                <PosEnumSelect
                  value={
                    compListingOnlyForSameZoneEnabled
                      ? RestrictSelectionType.SameZone
                      : RestrictSelectionType.SpecificSections
                  }
                  disabled={disabled}
                  valueOptionsContent={restrictSelectionTypes}
                  placeholderText={ContentId.None}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(v) => {
                    let compListingOnlyForSameZoneEnabledNew = false;
                    let compListingOnlyForSelectedSectionsEnabledNew = true;
                    if (v === RestrictSelectionType.SameZone) {
                      compListingOnlyForSameZoneEnabledNew = true;
                      compListingOnlyForSelectedSectionsEnabledNew = false;
                    }

                    setValue(
                      'compListingOnlyForSameZoneEnabled',
                      compListingOnlyForSameZoneEnabledNew
                    );
                    setSelectedSectionsEnabledAndSectionSettings(
                      compListingOnlyForSelectedSectionsEnabledNew
                    );
                  }}
                />
              }
            />
          )}
        </>
      )}

      {compListingOnlyForSelectedSectionsEnabled && venueMapSections && (
        <>
          <PricingSetting
            header={<Content id={ContentId.Sections} />}
            detail={
              <AutoPricingSelectedSectionSettingsInput
                venueMapSections={venueMapSections}
                disabled={disabled}
              />
            }
          />
          <PricingSetting
            header={<Content id={ContentId.RowComps} />}
            detail={
              <div style={{ width: '50%' }}>
                <AutoPricingCompListingRowOffset
                  listingId={listing?.id}
                  rowOffset={compListingSelectedSectionSettings?.rowOffset}
                  onRowOffsetChange={onRowOffsetChange}
                />
              </div>
            }
          />
        </>
      )}

      {!hideCompListingQuantityFilters && (
        <PricingSetting
          header={<Content id={ContentId.Quantities} />}
          detail={
            <div style={{ width: '50%' }}>
              <AutoPricingCompQuantitiesSelectorV2
                listing={listing}
                disabled={disabled}
                quantityFilters={compListingQuantityFilters}
                quantityScoreAdjustmentOverrideJson={
                  compListingQuantityScoreAdjustmentOverrideJson
                }
                onUpdateQuantityFilters={(
                  quantityFilters,
                  quantityFilterScoreAdjustmentOverrideJson
                ) => {
                  setValue('compListingQuantityFilters', quantityFilters);
                  setValue(
                    'compListingQuantityScoreAdjustmentOverrideJson',
                    quantityFilterScoreAdjustmentOverrideJson
                  );
                }}
                rootStyle={{ width: '100%' }}
              />
            </div>
          }
        />
      )}

      {!hideUnderCutAmountInput && (
        <PricingSetting
          header={<Content id={ContentId.HowToDeterminePrice} />}
          detail={
            <AutoPricingUnderCutRelativeAndAbsoluteAmountsInput
              uiCurrency={uiCurrency}
              disabled={disabled}
            />
          }
        />
      )}

      {children}
    </AutoPricingSettingsSection>
  );
};

const AdvancedSettingsSection = ({
  outlierModes,
  outlierModesDisabled,
  disabled,
  hideBaseAdvancedSettingElements,
  hideUnmappedInventoryToggle,
  children,
}: React.PropsWithChildren & {
  outlierModes: Record<string, ContentId>;
  outlierModesDisabled: Record<string, ContentId | undefined>;
  disabled: boolean;
  hideBaseAdvancedSettingElements?: boolean;
  hideUnmappedInventoryToggle?: boolean;
}) => {
  const { isDarkMode } = useSiteTheme();
  const hasUnmappedInvToggle = useUserHasFeature(Feature.UnmappedInvToggle);

  const { watch, setValue } = useFormContext<AutoPricingInputs>();

  const {
    compListingExcludeFanInventory,
    compListingExcludeDefects,
    compListingIncludeUnmappedInventory,
    outlierMode,
  } = watch();

  const shouldShowAdvancedSettings =
    !hideBaseAdvancedSettingElements || !!children;
  return (
    shouldShowAdvancedSettings && (
      <AutoPricingSettingsSection title={ContentId.AdvancedSettings}>
        {!hideBaseAdvancedSettingElements && (
          <>
            <PricingSetting
              header={
                <Stack gap="s" alignItems="center">
                  <Content id={ContentId.IncludeFanInventory} />
                  <Tooltip
                    triggerContent={
                      <div style={{ display: 'flex' }}>
                        <InfoOutlineIcon
                          withHoverEffect
                          size={vars.iconSize.m}
                        />
                      </div>
                    }
                    contentVariant={isDarkMode ? 'light' : 'dark'}
                  >
                    <Content id={ContentId.IncludeFanInventoryInfo} />
                  </Tooltip>
                </Stack>
              }
              detail={
                <Switch
                  disabled={disabled}
                  checked={!(compListingExcludeFanInventory ?? false)}
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={(isChecked) => {
                    const compListingExcludeFanInventoryNew = !isChecked;
                    if (
                      compListingExcludeFanInventoryNew !==
                      compListingExcludeFanInventory
                    ) {
                      setValue(
                        'compListingExcludeFanInventory',
                        compListingExcludeFanInventoryNew
                      );
                    }
                  }}
                />
              }
            />

            <PricingSetting
              header={<Content id={ContentId.IncludeDefects} />}
              detail={
                <Switch
                  disabled={disabled}
                  checked={!(compListingExcludeDefects ?? true)}
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={(isChecked) => {
                    const compListingExcludeDefectsNew = !isChecked;
                    if (
                      compListingExcludeDefectsNew !== compListingExcludeDefects
                    ) {
                      setValue(
                        'compListingExcludeDefects',
                        compListingExcludeDefectsNew
                      );
                    }
                  }}
                />
              }
            />

            {hasUnmappedInvToggle && !hideUnmappedInventoryToggle && (
              <PricingSetting
                header={<Content id={ContentId.UnmappedInventory} />}
                detail={
                  <Switch
                    disabled={disabled}
                    checked={compListingIncludeUnmappedInventory ?? false}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={(isChecked) => {
                      if (isChecked !== compListingIncludeUnmappedInventory) {
                        setValue(
                          'compListingIncludeUnmappedInventory',
                          isChecked
                        );
                      }
                    }}
                  />
                }
              />
            )}

            <PricingSetting
              header={<Content id={ContentId.OutlierMode} />}
              detail={
                <AutoPricingOutlierDetectionModeSelector
                  disabled={disabled}
                  outlierModes={outlierModes}
                  outlierModesDisabled={outlierModesDisabled}
                />
              }
            />

            {outlierMode != AutoPricingOutlierMode.SkipLogic && (
              <PricingSetting
                header={<Content id={ContentId.OutlierFiltering} />}
                detail={
                  <AutoPricingOutlierDetectionSettingsInput
                    disabled={disabled}
                  />
                }
              />
            )}
          </>
        )}

        {children}
      </AutoPricingSettingsSection>
    )
  );
};

const UndercuttingSection = ({
  uiCurrency,
  hideUnderCutAmountInput,
  disabled,
  children,
}: React.PropsWithChildren & {
  uiCurrency: UiCurrency;
  hideUnderCutAmountInput: boolean | undefined;
  disabled: boolean;
}) => {
  const { watch, setValue } = useFormContext<AutoPricingInputs>();
  const { undercutMode, undercutReferenceListingPosition } = watch();

  const showReferenceListingPositionInput = useMemo(() => {
    return (
      undercutMode !== AutoPricingUndercutMode.Simple ||
      !!undercutReferenceListingPosition
    );
  }, [undercutMode, undercutReferenceListingPosition]);

  return (
    <AutoPricingSettingsSection title={ContentId.Undercutting}>
      <PricingSetting
        header={<Content id={ContentId.SetReferenceListingTo} />}
        detail={
          <AutoPricingUnderCutModeSelector
            undercutMode={undercutMode}
            undercutReferenceListingPosition={undercutReferenceListingPosition}
            setUndercutModeValues={(
              newMode: AutoPricingUndercutMode | null,
              newPosition: number | null
            ) => {
              setValue('undercutMode', newMode);
              setValue('undercutReferenceListingPosition', newPosition);
            }}
            disabled={disabled}
          />
        }
      />

      {showReferenceListingPositionInput && (
        <PricingSetting
          header={<Content id={ContentId.ReferenceThe} />}
          detail={
            <AutoPricingUnderCutReferenceListingPositionInput
              undercutMode={undercutMode}
              undercutReferenceListingPosition={
                undercutReferenceListingPosition
              }
              setUndercutReferenceListingPosition={(
                newPosition: number | null
              ) => {
                setValue('undercutReferenceListingPosition', newPosition);
              }}
              disabled={disabled}
            />
          }
        />
      )}

      {!hideUnderCutAmountInput && (
        <PricingSetting
          header={<Content id={ContentId.HowToDeterminePrice} />}
          detail={
            <AutoPricingUnderCutRelativeAndAbsoluteAmountsInput
              uiCurrency={uiCurrency}
              disabled={disabled}
            />
          }
        />
      )}

      {children}
    </AutoPricingSettingsSection>
  );
};

const AutoPricingSettingsSection = ({
  title,
  children,
}: React.PropsWithChildren & { title: ContentId }) => {
  return (
    <Stack className={styles.sectionContainer} direction="column" gap="l">
      <div className={styles.sectionHeader}>
        <Content id={title} />
      </div>

      {children}
    </Stack>
  );
};

export const PricingSetting = ({
  header,
  detail,
  headerStyle,
}: {
  header: React.ReactNode;
  detail: React.ReactNode;
  headerStyle?: React.CSSProperties;
}) => {
  return (
    <Stack direction="row" gap="m" alignItems="center" width="full">
      <div className={styles.pricingSettingHeader} style={headerStyle}>
        {header}
      </div>
      <div className={styles.pricingSettingDetails}>{detail}</div>
    </Stack>
  );
};
